import { isKnownFontId, getFace } from '@/catalogs/fonts';
import { isKnownPaletteId } from '@/catalogs/palettes';
import { getTemplate, isKnownTemplateId } from '@/catalogs/templates';
import { isSafeHexColor } from '@/core/layout/geometry';
import { MIN_QUIET_ZONE, type Project } from '@/core/project/types';

/**
 * Сохранение и загрузка проекта в JSON.
 *
 * Импорт проверяется runtime-схемой: версия, набор полей, конечные числа,
 * диапазоны, допустимые цвета и известные идентификаторы. Ничего не
 * исполняется, произвольный SVG или HTML шаблоном стать не может.
 */

export const PROJECT_SCHEMA_VERSION = 1;

export type ImportResult =
  | { status: 'ok'; project: Project; notes: string[] }
  | { status: 'error'; message: string };

const ALIGNS = ['left', 'center', 'right'] as const;
const FORMATS = ['png', 'jpeg', 'svg'] as const;
const ECC = ['L', 'M', 'Q', 'H'] as const;
const KINDS = ['url', 'text'] as const;

export const OUTPUT_WIDTH_RANGE = { min: 128, max: 2048 } as const;
export const CAPTION_SIZE_RANGE = { min: 8, max: 160 } as const;
export const QUIET_ZONE_RANGE = { min: MIN_QUIET_ZONE, max: 16 } as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clampInt(value: unknown, range: { min: number; max: number }, fallback: number): number {
  if (!finite(value)) return fallback;
  return Math.min(range.max, Math.max(range.min, Math.round(value)));
}

function oneOf<T extends readonly string[]>(value: unknown, list: T, fallback: T[number]): T[number] {
  return typeof value === 'string' && (list as readonly string[]).includes(value)
    ? (value as T[number])
    : fallback;
}

export function serializeProject(project: Project): string {
  return `${JSON.stringify(project, null, 2)}\n`;
}

/**
 * Разбор файла проекта. Неизвестные поля отбрасываются, выходящие за диапазон
 * значения зажимаются, а обо всех расхождениях сообщается в notes.
 */
export function parseProjectFile(text: string, fallback: Project): ImportResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { status: 'error', message: 'Файл не является корректным JSON.' };
  }
  if (!isRecord(data)) return { status: 'error', message: 'Ожидался объект проекта.' };

  if (data.schemaVersion !== PROJECT_SCHEMA_VERSION) {
    return {
      status: 'error',
      message: `Версия схемы ${String(data.schemaVersion)} не поддерживается. Текущая версия — ${PROJECT_SCHEMA_VERSION}.`,
    };
  }

  const notes: string[] = [];
  const payloadRaw = isRecord(data.payload) ? data.payload : {};
  const qrRaw = isRecord(data.qr) ? data.qr : {};
  const templateRaw = isRecord(data.template) ? data.template : {};
  const captionRaw = isRecord(data.caption) ? data.caption : {};
  const outputRaw = isRecord(data.output) ? data.output : {};

  if (typeof payloadRaw.value !== 'string') {
    return { status: 'error', message: 'В проекте нет содержимого QR (payload.value).' };
  }

  const templateId = isKnownTemplateId(str(templateRaw.id))
    ? str(templateRaw.id)
    : ((): string => {
        notes.push(`Шаблон «${str(templateRaw.id, 'без имени')}» неизвестен, взят шаблон по умолчанию.`);
        return fallback.template.id;
      })();
  const template = getTemplate(templateId);
  if (finite(templateRaw.version) && templateRaw.version !== template.version) {
    notes.push(
      `Проект сохранён с версией шаблона ${String(templateRaw.version)}, сейчас используется ${template.version}. Оформление могло измениться.`,
    );
  }

  const paletteId = isKnownPaletteId(str(data.paletteId))
    ? str(data.paletteId)
    : ((): string => {
        if (str(data.paletteId) !== '') {
          notes.push(`Палитра «${str(data.paletteId)}» неизвестна, взята палитра по умолчанию.`);
        }
        return fallback.paletteId;
      })();

  const fontId = isKnownFontId(str(captionRaw.fontId))
    ? str(captionRaw.fontId)
    : ((): string => {
        if (str(captionRaw.fontId) !== '') {
          notes.push(`Шрифт «${str(captionRaw.fontId)}» неизвестен, взят шрифт по умолчанию.`);
        }
        return fallback.caption.fontId;
      })();

  const weight = clampInt(captionRaw.weight, { min: 100, max: 900 }, fallback.caption.weight);
  const face = getFace(fontId, weight);
  const savedRevision = str(captionRaw.fontRevision);
  if (savedRevision !== '' && savedRevision !== face.revision) {
    notes.push(
      'Версия файла шрифта отличается от сохранённой в проекте. Ширина строк могла измениться — проверьте макет.',
    );
  }

  const foreground = isSafeHexColor(qrRaw.foreground) ? qrRaw.foreground : fallback.qr.foreground;
  const background = isSafeHexColor(qrRaw.background) ? qrRaw.background : fallback.qr.background;
  if (!isSafeHexColor(qrRaw.foreground) || !isSafeHexColor(qrRaw.background)) {
    notes.push('Цвета QR заданы в неподдерживаемом формате, использованы значения по умолчанию.');
  }

  const project: Project = {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    payload: {
      kind: oneOf(payloadRaw.kind, KINDS, 'text'),
      value: payloadRaw.value,
    },
    qr: {
      errorCorrection: oneOf(qrRaw.errorCorrection, ECC, fallback.qr.errorCorrection),
      quietZoneModules: clampInt(qrRaw.quietZoneModules, QUIET_ZONE_RANGE, MIN_QUIET_ZONE),
      foreground,
      background,
    },
    template: { id: templateId, version: template.version },
    paletteId,
    title: str(data.title).slice(0, 300),
    caption: {
      text: str(captionRaw.text).slice(0, 300),
      fontId,
      fontRevision: face.revision,
      weight: face.weight,
      size: clampInt(captionRaw.size, CAPTION_SIZE_RANGE, fallback.caption.size),
      align: oneOf(captionRaw.align, ALIGNS, fallback.caption.align),
    },
    note: str(data.note).slice(0, 500),
    output: {
      widthPx: clampInt(outputRaw.widthPx, OUTPUT_WIDTH_RANGE, fallback.output.widthPx),
      format: oneOf(outputRaw.format, FORMATS, fallback.output.format),
      includeDecoration:
        typeof outputRaw.includeDecoration === 'boolean'
          ? outputRaw.includeDecoration
          : fallback.output.includeDecoration,
      jpegQuality: finite(outputRaw.jpegQuality)
        ? Math.min(1, Math.max(0.3, outputRaw.jpegQuality))
        : fallback.output.jpegQuality,
      transparentBackground:
        typeof outputRaw.transparentBackground === 'boolean'
          ? outputRaw.transparentBackground
          : fallback.output.transparentBackground,
    },
  };

  return { status: 'ok', project, notes };
}

/** Имя файла проекта не включает содержимое QR: оно может быть чувствительным. */
export function projectFileName(at: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `kurlyk-project-${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}-${pad(at.getHours())}${pad(at.getMinutes())}.json`;
}
